/*
 * AppNav — the shared top navigation used on every full-page view (Tax,
 * Insights, Money, Account…) so the menu flows the same way it does on the
 * dashboard. Map and Manage are dashboard overlays, so they link back to /app
 * with ?open=… which the dashboard honours on load.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App.jsx';
import { taxPackAvailable } from '../global.js';
import { TopViewSwitch } from './simple/viewMode.jsx';

// Avatar initials: the person's name first, email only as a fallback - never two
// arbitrary letters of an email address (e.g. 'MA' from mare@...).
function initials(me) {
  const name = ((me?.first_name || '') + ' ' + (me?.last_name || '')).trim();
  if (name) return name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return (me?.email || 'U').slice(0, 2).toUpperCase();
}

const ITEMS = [
  ['dashboard', '🏠 Dashboard', '/app'],
  ['map', '🗺 Map', '/app?open=map'],
  ['insights', '📈 Insights', '/insights'],
  ['tax', '🧾 Tax', '/tax'],
  ['manage', '⚙️ Manage', '/manage'],
];

export default function AppNav({ active }) {
  const { me, logout } = useAuth();
  // The avatar is a real menu here too (Account / Sign out), so it behaves the
  // same on Manage / Insights / Tax as it does on the dashboard - previously it
  // was a bare link, so on those pages the "menu" never opened. Close on an
  // outside click or Escape: a menu that opens should never feel stuck.
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <header className="flex items-center justify-between gap-3 py-3 px-4 md:px-8 sticky top-0 bg-ink/95 backdrop-blur z-20 border-b border-edge">
      <Link to="/app" className="flex items-center gap-2 shrink-0">
        <img src="/logo.svg" alt="" className="w-8 h-8" />
        <b className="text-lg">Vault</b>
      </Link>
      <nav className="flex items-center gap-1 text-sm flex-wrap justify-end">
        {ITEMS.filter(([key]) => key !== 'tax' || taxPackAvailable()).map(([key, label, to]) => (
          <Link key={key} to={to}
                className={`px-3 py-1.5 rounded-lg transition ${active === key
                  ? 'text-vblue bg-vblue/10 border border-vblue/40'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
            {label}
          </Link>
        ))}
        {me?.is_superuser && (
          <Link to="/revenue"
                className={`px-3 py-1.5 rounded-lg transition ${active === 'revenue'
                  ? 'text-vblue bg-vblue/10 border border-vblue/40'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
            💰 Revenue
          </Link>
        )}
        {/* #476 — a deep link opened this full screen while Simple view is on: offer the way back. */}
        {me?.view_mode === 'simple' && <TopViewSwitch backToSimple />}
        <Link to="/app" className="btn-primary !py-1.5 !px-3.5 text-sm ml-1">＋ New</Link>
        <div className="relative ml-1" ref={menuRef}>
          <button type="button" title="Account & options"
                  className="w-8 h-8 rounded-full bg-vault-gradient grid place-items-center text-[#06121f] font-extrabold text-xs"
                  onClick={() => setOpen((v) => !v)}>
            {initials(me)}
          </button>
          {open && (
            <div className="absolute right-0 mt-2 w-56 bg-surface border border-edge2 rounded-xl p-1.5 shadow-2xl z-50">
              <Link to="/account" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5"
                    onClick={() => setOpen(false)}>👤 <span>Account &amp; profile</span></Link>
              {me?.is_superuser && (
                <>
                  <div className="h-px bg-edge my-1 mx-1"></div>
                  <Link to="/support" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-vpurple"
                        onClick={() => setOpen(false)}>🛟 <span>Support desk</span></Link>
                  <Link to="/testing" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-vblue"
                        onClick={() => setOpen(false)}>🧪 <span>Testing</span></Link>
                </>
              )}
              <div className="h-px bg-edge my-1 mx-1"></div>
              <button type="button"
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-red-400 text-left"
                      onClick={() => { setOpen(false); logout(); }}>↩ <span>Sign out</span></button>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}
